import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Loading from "../common/Loading";
import { api, errorMessage, ROLES, GENRES, Field, Chip, SecondaryButton, inputClass, ringStyle, clean, filtered } from "./shared";
import { ImageUploadButton } from "./uploaders";
import Dropdown from "./Dropdown";
import StudioShell from "./StudioShell";

const ensureUrl = (v) => {
  const value = (v || "").trim();
  if (!value) return "";
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};
const display = (v) => (v || "").replace(/^https?:\/\//i, "").replace(/\/$/, "");
const isValidUrl = (v) => !v.trim() || /^https?:\/\/\S+\.\S+/.test(ensureUrl(v));

const formatCount = (n) => {
  if (n === null || n === undefined) return "—";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(n);
};

const LINKS = [
  ["spotify", "Spotify"],
  ["youtube", "YouTube"],
  ["instagram", "Instagram"],
  ["website", "Website"],
];

function Banner({ cover, avatar, name, verified, subline, children }) {
  return (
    <div className="rounded-3xl border border-gray-200 bg-white overflow-hidden">
      <div className="h-32 md:h-44 relative" style={{ backgroundColor: "#cdd6d0" }}>
        {cover && <img src={cover} alt="" className="w-full h-full object-cover" />}
        {children}
      </div>
      <div className="px-5 md:px-8 pb-6 flex items-end gap-5">
        <div className="relative z-10 -mt-12 w-24 h-24 md:w-28 md:h-28 rounded-full border-4 border-white overflow-hidden shrink-0 flex items-center justify-center text-4xl font-bold text-white" style={{ backgroundColor: "#93a89b" }}>
          {avatar ? <img src={avatar} alt={name} className="w-full h-full object-cover" /> : (name || "?").charAt(0).toUpperCase()}
        </div>
        <div className="pt-4 min-w-0">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 flex flex-wrap items-center gap-3">
            <span className="truncate">{name}</span>
            {verified && (
              <span className="rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold px-3 py-1">Verified artist</span>
            )}
          </h2>
          <p className="text-gray-500 text-sm">{subline}</p>
        </div>
      </div>
    </div>
  );
}

function ProfileView({ profile, onEdit }) {
  const roleLabel = ROLES.find((r) => r.value === profile.primary_role)?.label;
  const subline = [profile.handle && `@${profile.handle}`, roleLabel, profile.location || profile.country].filter(Boolean).join(" · ");
  const links = LINKS.filter(([k]) => profile.social_links?.[k]);
  const stats = [
    ["Followers", profile.stats.followers],
    ["Monthly listeners", profile.stats.monthly_listeners],
    ["Tracks", profile.stats.tracks],
    ["Releases", profile.stats.releases],
  ];
  const deactivated = profile.account_status === "deactivated";

  return (
    <div className="max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-[28px] leading-tight font-bold text-gray-900">Artist Profile</h1>
        <div className="flex gap-3">
          {profile.public_slug ? (
            <Link to={`/artists/${profile.public_slug}`} className="rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50">
              View public page
            </Link>
          ) : (
            <span title="Available once your artist profile is published" aria-disabled="true" className="rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-400 cursor-not-allowed">
              View public page
            </span>
          )}
          {!deactivated && (
            <Link to="/artist-studio/public-page" className="rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50">
              Public page settings
            </Link>
          )}
          {!deactivated && (
            <button type="button" onClick={onEdit} className="rounded-full px-5 py-2.5 text-sm font-semibold text-white" style={{ backgroundColor: "#143328" }}>
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {deactivated && (
        <div className="mb-5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3">
          Your artist account is deactivated, so the profile is read-only.
        </div>
      )}

      <Banner cover={profile.cover_image_url} avatar={profile.profile_image_url} name={profile.artist_name} verified={profile.verified} subline={subline} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        {stats.map(([label, value]) => (
          <div key={label} className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{formatCount(value)}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Bio</h3>
          {profile.bio ? (
            <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">{profile.bio}</p>
          ) : (
            <p className="text-sm text-gray-400">No bio yet.</p>
          )}
          {profile.genres?.length > 0 && (
            <>
              <h3 className="text-sm font-bold text-gray-900 mt-5 mb-3">Genres</h3>
              <div className="flex flex-wrap gap-2">
                {profile.genres.map((g) => (
                  <span key={g} className="rounded-full text-white text-xs font-semibold px-3 py-1.5" style={{ backgroundColor: "#1f5a3a" }}>{g}</span>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Links</h3>
          {links.length === 0 ? (
            <p className="text-sm text-gray-400">No links yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {links.map(([k, label]) => (
                <li key={k} className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-4 py-2 text-sm">
                  <span className="text-gray-900">{label}</span>
                  <a href={ensureUrl(profile.social_links[k])} target="_blank" rel="noopener noreferrer" title={display(profile.social_links[k])} className="block min-w-0 truncate text-left" style={{ color: "#1f5a3a" }}>
                    {display(profile.social_links[k])}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ProfileEdit({ profile, onCancel, onSaved }) {
  const {
    register, handleSubmit, watch, setValue, getValues, setError,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      artist_name: profile.artist_name || "",
      handle: profile.handle || "",
      primary_role: profile.primary_role || "musician",
      location: profile.location || "",
      genres: profile.genres || [],
      bio: profile.bio || "",
      spotify: profile.social_links?.spotify || "",
      youtube: profile.social_links?.youtube || "",
      instagram: profile.social_links?.instagram || "",
      website: profile.social_links?.website || "",
      cover_image_url: profile.cover_image_url || "",
      profile_image_url: profile.profile_image_url || "",
    },
  });
  const genres = watch("genres");
  const cover = watch("cover_image_url");
  const avatar = watch("profile_image_url");
  const name = watch("artist_name");

  const toggleGenre = (g) => {
    const current = getValues("genres");
    // shouldDirty is needed: without it the form never notices that the genres changed ("You haven't changed anything").
    if (current.includes(g)) setValue("genres", current.filter((x) => x !== g), { shouldDirty: true });
    else if (current.length < 8) setValue("genres", [...current, g], { shouldDirty: true });
  };

  // Server field name -> form field name.
  const SERVER_FIELDS = {
    artist_name: "artist_name", handle: "handle", location: "location", bio: "bio", genres: "genres",
    "social_links.spotify": "spotify", "social_links.youtube": "youtube",
    "social_links.instagram": "instagram", "social_links.website": "website",
    profile_image_url: "profile_image_url", cover_image_url: "cover_image_url",
  };

  const onSubmit = async (d) => {
    // Send only what the artist actually changed, so other saved fields are never overwritten.
    const payload = {};
    if (dirtyFields.artist_name) payload.artist_name = d.artist_name.trim();
    if (dirtyFields.handle) payload.handle = d.handle.trim().replace(/^@/, "");
    if (dirtyFields.primary_role) payload.primary_role = d.primary_role;
    if (dirtyFields.location) payload.location = d.location.trim();
    const savedGenres = [...(profile.genres || [])].sort().join("|");
    if ([...d.genres].sort().join("|") !== savedGenres) payload.genres = d.genres;
    if (dirtyFields.bio) payload.bio = d.bio.trim();
    if (dirtyFields.cover_image_url) payload.cover_image_url = d.cover_image_url;
    if (dirtyFields.profile_image_url) payload.profile_image_url = d.profile_image_url;
    if (LINKS.some(([k]) => dirtyFields[k])) {
      payload.social_links = {
        spotify: ensureUrl(d.spotify), youtube: ensureUrl(d.youtube),
        instagram: ensureUrl(d.instagram), website: ensureUrl(d.website),
      };
    }
    if (Object.keys(payload).length === 0) {
      toast.info("You haven't changed anything.");
      return;
    }
    try {
      const response = await api.post("update-profile", payload);
      if (response.data?.success) {
        toast.success("Your artist profile has been saved.");
        onSaved(response.data.result);
      }
    } catch (error) {
      // The form is left as-is so nothing the artist typed is lost; they can fix it and retry.
      const fieldErrors = error?.response?.data?.errors;
      if (error?.response?.status === 400 && fieldErrors) {
        let unmapped = false;
        Object.entries(fieldErrors).forEach(([key, message]) => {
          const field = SERVER_FIELDS[key];
          if (field) setError(field, { type: "server", message });
          else unmapped = true;
        });
        toast.error(unmapped ? Object.values(fieldErrors).join(" ") : "Please fix the highlighted fields.");
      } else {
        toast.error(errorMessage(error, "We couldn't save your profile right now. Your changes are still here - please try again."));
      }
    }
  };

  const roleLabel = ROLES.find((r) => r.value === getValues("primary_role"))?.label;

  return (
    <div className="max-w-4xl">
      <h1 className="text-[28px] leading-tight font-bold text-gray-900 mb-6">Artist Profile</h1>
      <Banner cover={cover} avatar={avatar} name={name || profile.artist_name} verified={profile.verified}
        subline={[profile.handle && `@${profile.handle}`, roleLabel, profile.location].filter(Boolean).join(" · ")}>
        <ImageUploadButton
          label="Change cover · 1500 × 500"
          onUploaded={(url) => setValue("cover_image_url", url, { shouldDirty: true })}
          className="absolute top-4 right-4 rounded-full bg-white/95 px-4 py-1.5 text-sm font-semibold hover:bg-white"
        />
      </Banner>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="rounded-2xl border border-gray-200 bg-white p-6 mt-5 space-y-5">
        <div className="flex items-center gap-4">
          <ImageUploadButton
            label={avatar ? "Change profile photo" : "Upload profile photo"}
            onUploaded={(url) => setValue("profile_image_url", url, { shouldDirty: true })}
            className="group relative w-14 h-14 shrink-0 rounded-full overflow-hidden bg-[#93a89b] cursor-pointer focus:outline-none focus:ring-2"
          >
            {avatar && <img src={avatar} alt="" className="w-full h-full object-cover" />}
            <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-[10px] font-semibold text-white opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100">
              {avatar ? "Change" : "Upload"}
            </span>
          </ImageUploadButton>
          <ImageUploadButton
            label={avatar ? "Change profile photo" : "Upload profile photo"}
            onUploaded={(url) => setValue("profile_image_url", url, { shouldDirty: true })}
            className="text-sm font-semibold"
          />
          {avatar && (
            <button type="button" onClick={() => setValue("profile_image_url", "", { shouldDirty: true })} className="text-sm text-gray-500 underline">
              Remove photo
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Display name / stage name" required error={errors.artist_name?.message}>
            <input type="text" className={inputClass(errors.artist_name)} style={ringStyle}
              {...register("artist_name", {
                validate: (v) => {
                  const t = v.trim();
                  if (!t) return "Display name is required.";
                  if (t.length < 2) return "Display name must be at least 2 characters.";
                  if (t.length > 150) return "Display name must be at most 150 characters.";
                  if (/[<>\u0000-\u001F\u007F]/.test(t)) return "Display name contains characters that are not allowed.";
                  return true;
                },
              })} />
          </Field>
          <Field label="Handle" error={errors.handle?.message}>
            <input type="text" className={inputClass(errors.handle)} style={ringStyle} placeholder="@yourhandle"
              {...filtered(register("handle", { validate: (v) => !v.trim() || /^@?[A-Za-z0-9_.]{3,30}$/.test(v.trim()) || "3-30 letters, numbers, dots or underscores." }), clean.handle, 31)} />
          </Field>
          <Field label="Primary role">
            <Dropdown
              ariaLabel="Primary role" options={ROLES} value={watch("primary_role")}
              onChange={(v) => setValue("primary_role", v, { shouldDirty: true })}
            />
          </Field>
        </div>

        <Field label="Location" error={errors.location?.message}>
          <input type="text" className={`${inputClass(errors.location)} md:w-1/3`} style={ringStyle} placeholder="City, Country"
            {...register("location", { maxLength: { value: 120, message: "At most 120 characters." } })} />
        </Field>

        <Field label="Genres" hint="Choose up to 8.">
          <div className="flex flex-wrap gap-2">
            {GENRES.map((g) => (<Chip key={g} selected={genres.includes(g)} onClick={() => toggleGenre(g)}>{g}</Chip>))}
          </div>
        </Field>

        <Field label="Bio" error={errors.bio?.message}>
          <textarea rows={4} className={inputClass(errors.bio)} style={ringStyle}
            {...register("bio", { maxLength: { value: 1000, message: "Bio must be at most 1000 characters." } })} />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {LINKS.slice(0, 3).map(([k, label]) => (
            <Field key={k} label={label} error={errors[k]?.message}>
              <input type="text" className={inputClass(errors[k])} style={ringStyle}
                {...register(k, { validate: (v) => isValidUrl(v) || "Enter a valid link." })} />
            </Field>
          ))}
        </div>
        <Field label="Website" error={errors.website?.message}>
          <input type="text" className={`${inputClass(errors.website)} md:w-1/3`} style={ringStyle}
            {...register("website", { validate: (v) => isValidUrl(v) || "Enter a valid link." })} />
        </Field>

        <div className="flex justify-end gap-3 pt-2">
          <SecondaryButton onClick={onCancel} disabled={isSubmitting}>Cancel</SecondaryButton>
          <button
            type="submit"
            disabled={isSubmitting || Object.keys(errors).length > 0}
            className="rounded-full font-semibold text-white px-8 py-3 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
            style={{ backgroundColor: isSubmitting || Object.keys(errors).length > 0 ? undefined : "#143328" }}
          >
            {isSubmitting ? "Saving..." : "Save Profile"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function ArtistProfilePage() {
  const [state, setState] = useState({ loading: true, profile: null, error: null });
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true, profile: null, error: null });
    try {
      const response = await api.get("profile");
      setState({ loading: false, profile: response.data?.result || null, error: null });
    } catch (error) {
      const status = error?.response?.status;
      setState({
        loading: false,
        profile: null,
        error: status === 403 ? "not-approved" : "failed",
      });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { loading, profile, error } = state;

  return (
    <StudioShell active="profile">
      {loading ? (
        <div className="flex items-center justify-center py-24"><Loading /></div>
      ) : error === "not-approved" ? (
        <p className="text-gray-600">
          Your artist profile is available once your application is approved.{" "}
          <Link to="/artist-studio" className="underline font-semibold">Back to Artist Studio</Link>
        </p>
      ) : error ? (
        <div className="py-8">
          <p className="text-gray-600 mb-4">We couldn't load your artist profile. Please try again.</p>
          <SecondaryButton onClick={load}>Retry</SecondaryButton>
        </div>
      ) : editing ? (
        <ProfileEdit
          profile={profile}
          onCancel={() => setEditing(false)}
          onSaved={(saved) => {
            setState((s) => ({ ...s, profile: { ...s.profile, ...saved } }));
            setEditing(false);
          }}
        />
      ) : (
        <ProfileView profile={profile} onEdit={() => setEditing(true)} />
      )}
    </StudioShell>
  );
}
